import React, { useState, useEffect, useRef, useMemo } from 'react';

import { useTranslation } from "core/hooks/useTranslation";
import useDevice from 'core/hooks/useDevice';
import usePermissions from 'core/hooks/usePermission';

import Form from "core/ui-kit/form";
import Spin from "core/ui-kit/spin";
import Divider from "core/ui-kit/divider"
import { Row, Col } from "core/ui-kit/layout";
import Checkbox from "core/ui-kit/checkbox";

import TabFormDashboardLayout from 'components/layouts/tab/form';
import FormBuilder from 'components/common/formBuilder';

import { PERMISSION_RESOURCE, PERMISSION_ACTION } from 'core/constants/permission';
import { USER_STATE } from 'core/constants/auth';
import { REG_FORM_CONTROL_RULE, AFFILIATE_REG_TYPE } from "core/constants/regForm";
import PASSWORD_SETUP_FLOW_TYPE from 'core/constants/project/passwordSetupFlowType';

import { FORM_FIELD_NAMES, SECTION_ITEMS } from './constants';

import FormRulesBuilder from 'core/helpers/common/formRulesBuilder';
import flagsToBinary from 'core/helpers/common/flagsToBinary';
import binaryToFlags from 'core/helpers/common/binaryToFlags';
import isFormChanged from 'core/helpers/form/isFormChanged';
import showSuccess from 'core/helpers/common/showSuccess';

import projectService from 'core/services/apiServices/projectService';
import autosuggestionService from 'core/services/apiServices/autosuggestionService';

import { authStore, globalCompanyIdSelector, globalProjectIdSelector, useAuthSelector } from 'core/stores/authStore';
import { globalStore } from 'core/stores/globalStore';

/** Project Edit Page Registration Form Tab Component */
const RegistrationFormComponent = ({
    onTabChange,
}) => {
    const { t } = useTranslation();

    const permissionUtils = usePermissions();

    const { isMobile } = useDevice();

    const [formInstance] = Form.useForm();
    const { validateFields, setFieldsValue } = formInstance;

    const [isFormTouched, setIsFormTouched] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const globalProjectId = useAuthSelector(globalProjectIdSelector);
    const globalCompanyId = useAuthSelector(globalCompanyIdSelector);

    const [affiliateRegistrationConfig, setAffiliateRegistrationConfig] = useState({});
    const [selectedRegistrationType, setSelectedRegistrationType] = useState([])
    const [affiliateManagersAutosuggestion, setAffiliateManagersAutosuggestion] = useState([])

    const registrationTypeOptions = useMemo(() => [
        { 
            label: t('backoffice.projects.selfSignUp'), 
            value: AFFILIATE_REG_TYPE.SELF_SIGN_UP, 
            disabled: selectedRegistrationType.length === 1 && selectedRegistrationType[0] === AFFILIATE_REG_TYPE.SELF_SIGN_UP
        },
        { 
            label: t('backoffice.projects.byAffiliateManager'), 
            value: AFFILIATE_REG_TYPE.BY_AFFILIATE_MANAGER,  
            disabled: selectedRegistrationType.length === 1 && selectedRegistrationType[0] === AFFILIATE_REG_TYPE.BY_AFFILIATE_MANAGER
        },
    ], [t, selectedRegistrationType] )

    const passwordSetupFlowOptions = useMemo(() => [
        { value: PASSWORD_SETUP_FLOW_TYPE.BY_EMAIL, text: t('backoffice.projects.byEmail') },
        { value: PASSWORD_SETUP_FLOW_TYPE.BY_MANAGER, text: t('backoffice.projects.byManager') }
    ], [ t ])

    const affiliateManagerOptions = useMemo(() => {
        return [
            { value: null, text: t("backoffice.common.none") },
            ...affiliateManagersAutosuggestion.map(({ name, id }) => ({ text: name, value: id }))
        ];
    }, [affiliateManagersAutosuggestion, t]);


    const formBuilderRef = useRef({
        handleForm: null,
        isFormTouched: null
    });

    useEffect(() => {
        autosuggestionService.getAffiliateManagers({
            state: flagsToBinary([USER_STATE.ACTIVE, USER_STATE.LOCKED_2_FA, USER_STATE.LOCKED_LOGIN])
        }).then(value => setAffiliateManagersAutosuggestion(value))
    }, [])

    useEffect(() => {
        setIsLoading(true);
        projectService.getProjectAffiliateRegistrationConfiguration()
            .then(value => {
                setAffiliateRegistrationConfig(value);

                const registrationTypeAsArray = binaryToFlags(Object.values(AFFILIATE_REG_TYPE), value.registrationType);

                setFieldsValue({
                    [FORM_FIELD_NAMES.DEFAULT_MANAGER_ID]: value.defaultAffiliateManagerId,
                    [FORM_FIELD_NAMES.PASSWORD_SETUP_FLOW]: value.passwordSetupFlow,
                    [FORM_FIELD_NAMES.REG_TYPE]: registrationTypeAsArray
                })
                
                setSelectedRegistrationType(registrationTypeAsArray)
            })
            .finally(() => {
                setIsLoading(false)
            })
    }, [])

    const handleFormValuesChange = (_, values) => {
        const initialValues = {
            [FORM_FIELD_NAMES.DEFAULT_MANAGER_ID]: affiliateRegistrationConfig.defaultAffiliateManagerId,
            [FORM_FIELD_NAMES.PASSWORD_SETUP_FLOW]: affiliateRegistrationConfig.passwordSetupFlow,
            [FORM_FIELD_NAMES.REG_TYPE]: affiliateRegistrationConfig.registrationType
        }
        const formValues = {
            [FORM_FIELD_NAMES.DEFAULT_MANAGER_ID]: values[FORM_FIELD_NAMES.DEFAULT_MANAGER_ID],
            [FORM_FIELD_NAMES.PASSWORD_SETUP_FLOW]: values[FORM_FIELD_NAMES.PASSWORD_SETUP_FLOW],
            [FORM_FIELD_NAMES.REG_TYPE]: flagsToBinary(values[FORM_FIELD_NAMES.REG_TYPE])
        }
        setIsFormTouched(isFormChanged(initialValues, formValues))
    }

    const handleFormTouch = (touched) => {
        onTabChange(touched)
    }

    const handleSave = formControlsFormValues => {
        validateFields()
            .then(data => {
                setIsSaving(true);
                projectService.saveProjectAffiliateRegistrationConfiguration({
                    defaultAffiliateManagerId: data[FORM_FIELD_NAMES.DEFAULT_MANAGER_ID],
                    registrationType: flagsToBinary(data[FORM_FIELD_NAMES.REG_TYPE]),
                    passwordSetupFlow: data[FORM_FIELD_NAMES.PASSWORD_SETUP_FLOW],
                    formControls: formControlsFormValues.formControls
                }).then(({value, message}) => {
                    setAffiliateRegistrationConfig(value);
                    setIsFormTouched(false);
                    showSuccess(message);

                    if(data[FORM_FIELD_NAMES.PASSWORD_SETUP_FLOW] !== affiliateRegistrationConfig.passwordSetupFlow){
                        authStore.updateUserProp("passwordSetupFlow", data[FORM_FIELD_NAMES.PASSWORD_SETUP_FLOW])
                    }

                    globalStore.updateRegistrationType(globalCompanyId, globalProjectId, value.registrationType);
                })
                .finally(() => setIsSaving(false))
            }).catch(() => { })
    };
    

    const handlePasswordSetupFlowChange = value => {
        setAffiliateRegistrationConfig(prevState => ({
            ...prevState,
            formControls: prevState.formControls.map(control => {
                if(control.name === "Email"){
                    return { 
                        ...control, 
                        rule: value === PASSWORD_SETUP_FLOW_TYPE.BY_MANAGER ? flagsToBinary(
                            [REG_FORM_CONTROL_RULE.REQUIRED, REG_FORM_CONTROL_RULE.OPTIONAL, REG_FORM_CONTROL_RULE.HIDDEN]
                        ) : [REG_FORM_CONTROL_RULE.REQUIRED],
                        selectedRule: value === PASSWORD_SETUP_FLOW_TYPE.BY_MANAGER ? control.selectedRule : REG_FORM_CONTROL_RULE.REQUIRED,
                    }
                }
                return control
            })
        }))
    }

    useEffect(() => {
        onTabChange(isFormTouched);
    }, [isFormTouched])

    return (
        <TabFormDashboardLayout
            buttons={
                [
                    {
                        type: "primary",
                        onClick: () => formBuilderRef.current.handleForm(),
                        text: t("backoffice.common.save"),
                        enabled: permissionUtils.has( PERMISSION_RESOURCE.PROJECT_REGISTRATION_CONFIGURATION, PERMISSION_ACTION.MODIFY ),
                        loading: isSaving,
                        disabled: !(formBuilderRef.current.isFormTouched || isFormTouched)
                    }
                ]
            }
        >
            <Spin spinning={isLoading}>
                <h4 className='rt--form-section-title rt--title rt--mb-16 rt--mt-8 rt--font-bold rt--font-biger'>
                    {t("backoffice.projects.affiliateRegConfig")}
                </h4>
                <Form
                    form={formInstance}
                    onValuesChange={handleFormValuesChange}
                >
                    <Row gutter={[16, 0]}>
                        <Col xs={24}>
                            <Form.Item
                                name={FORM_FIELD_NAMES.REG_TYPE}
                                rules={
                                    new FormRulesBuilder(t).required({whitespace: false}).build()
                                }
                            >
                                <Checkbox.Group
                                    onChange={setSelectedRegistrationType}
                                    options={registrationTypeOptions}
                                />
                            </Form.Item>
                        </Col>

                        <Col xs={24} sm={8}>
                            <Form.Select
                                label={t('backoffice.projects.defaultManager')}
                                placeholder={`${t('backoffice.common.select')} ${t('backoffice.projects.defaultManager')}`}
                                name={FORM_FIELD_NAMES.DEFAULT_MANAGER_ID}
                                search={true}
                                options={affiliateManagerOptions}
                            />
                            <div className='rt--text-lightest rt--font-small rt--font-regular rt--font-italic rt--mt-8'>
                                { t("backoffice.projects.affiliateManagerTextInfo") }
                            </div>
                        </Col>

                        <Col xs={24} sm={8}>
                            <Form.Select
                                label={t('backoffice.projects.passwordSetupFlow')}
                                name={FORM_FIELD_NAMES.PASSWORD_SETUP_FLOW}
                                options={passwordSetupFlowOptions}
                                onChange={handlePasswordSetupFlowChange}
                            />
                        </Col>
                    </Row>
                </Form>

                {
                    isMobile && <Divider />
                }

                <FormBuilder
                    sections={SECTION_ITEMS}
                    onTabChange={handleFormTouch}
                    ref={formBuilderRef}
                    formData={affiliateRegistrationConfig.formControls ?? []}
                    userCanModify={ permissionUtils.has( PERMISSION_RESOURCE.PROJECT_REGISTRATION_CONFIGURATION, PERMISSION_ACTION.MODIFY )}
                    onFormFinish={handleSave}
                    fieldNameRender={fieldName => t("backoffice.registrationform." + fieldName)}
                />
            </Spin>
        </TabFormDashboardLayout>
    )
}

export default RegistrationFormComponent;
